import { useState } from "react";
import { Modal } from "semantic-ui-react";

import { useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import { WEBHOOK_TRIGGERS } from "models";
import { WebhooksTestForm } from "modules/WebhooksDashboard/components/WebhooksTestModal/components";
import { useTestWebhookMutation } from "modules/WebhooksDashboard/components/WebhooksTestModal/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { Webhook } from "models";

type WebhooksTestModalProps = {
  webhook: Webhook | null;
  onClose: () => void;
};

export type TestWebhookFormPayload = {
  appointment_id: number | null;
  communication_key: string;
  customer_dms_nr: string;
  customer_type: string;
  dealer_location_id: number | null;
  wo_nr: string;
  user_id: number | null;
  dealer_name: string;
  dealer_id: number | null;
  location_name: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: number | null;
  web_version: string;
  app_version: string;
  last_login: Date | null;
  active: boolean;
  has_leased_tablet: boolean;
  is_combi_user: boolean;
  is_counter_tablet_user: boolean;
};

const FormHelper = createFormHelper<TestWebhookFormPayload>();

export const WebhooksTestModal = ({ onClose, webhook }: WebhooksTestModalProps) => {
  const [hasErrors, setHasErrors] = useState(false);

  const { selectedDealer } = useSelectedDealer();
  const { mutateAsync: testWebhook, isPending } = useTestWebhookMutation();

  const isUserTrigger = webhook?.webhook_trigger_id === WEBHOOK_TRIGGERS.User;

  const handleClose = () => {
    onClose();
    setHasErrors(false);
  };

  const validateFormData = (values: TestWebhookFormPayload) => {
    const hasErrors = isUserTrigger ? !values.user_id || !values.dealer_location_id : !values.appointment_id || !values.dealer_location_id || !values.wo_nr;
    setHasErrors(hasErrors);
    return hasErrors;
  };

  const handleSubmit = async (values: TestWebhookFormPayload) => {
    if (validateFormData(values)) return;

    await testWebhook({
      payload: {
        ...(values.appointment_id && { appointment_id: Number(values.appointment_id) }),
        communication_key: values.communication_key,
        customer_dms_nr: values.customer_dms_nr,
        customer_type: values.customer_type,
        dealer_location_id: Number(values.dealer_location_id) || 0,
        wo_nr: values.wo_nr,
        dealer_id: selectedDealer?.id || 0,
        webhook_trigger_id: webhook?.webhook_trigger_id || 0,
        ...(isUserTrigger && {
          user: {
            user_id: Number(values.user_id) || 0,
            dealer_location_id: Number(values.dealer_location_id) || 0,
            active: values.active,
            has_leased_tablet: values.has_leased_tablet,
            is_combi_user: values.is_combi_user,
            is_counter_tablet_user: values.is_counter_tablet_user,
            ...(values.dealer_name && { dealer_name: values.dealer_name }),
            ...(values.dealer_id && { dealer_id: Number(values.dealer_id) }),
            ...(values.location_name && { location_name: values.location_name }),
            ...(values.username && { username: values.username }),
            ...(values.email && { email: values.email }),
            ...(values.first_name && { first_name: values.first_name }),
            ...(values.last_name && { last_name: values.last_name }),
            ...(values.role && { role: values.role }),
            ...(values.web_version && { web_version: values.web_version }),
            ...(values.app_version && { app_version: values.app_version }),
            ...(values.last_login && { last_login: formatDate(values.last_login) })
          }
        })
      },
      webhook_id: webhook?.id || 0
    });
    handleClose();
  };

  return (
    <Modal open={!!webhook} size="large" scrolling>
      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          appointment_id: null,
          communication_key: "",
          customer_dms_nr: "",
          customer_type: "",
          dealer_location_id: null,
          wo_nr: "",
          user_id: null,
          dealer_name: "",
          dealer_id: null,
          location_name: "",
          username: "",
          email: "",
          first_name: "",
          last_name: "",
          role: null,
          web_version: "",
          app_version: "",
          last_login: null,
          active: false,
          has_leased_tablet: false,
          is_combi_user: false,
          is_counter_tablet_user: false
        }}
      >
        <WebhooksTestForm hasErrors={hasErrors} isPending={isPending} isUserTrigger={isUserTrigger} onClose={handleClose} onSubmit={handleSubmit} />
      </FormHelper.Form>
    </Modal>
  );
};
